import { useState } from "react";
import type { JSX } from "react";

import {
  BrandMark,
  Icon,
  PageSectionHeader,
  resolveBrand,
} from "../../components/ui";
import { ConfigureSourceDialog } from "./ConfigureSourceDialog";
import {
  useSourceAppConfig,
  type SourceAppConfigEntry,
} from "./hooks/useSourceAppConfig";

/// The Sources panel — one list, exactly like Accounts. Every source is the
/// same: it appears here ONLY once it is configured; not configured → not shown.
/// "+" picks any not-yet-configured source and opens the one config dialog;
/// clicking a configured source opens the same dialog to edit it. No
/// shared-provider vs deployment split, no per-source special cases.

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function SourceCard({
  entry,
  onClick,
}: {
  readonly entry: SourceAppConfigEntry;
  readonly onClick: () => void;
}): JSX.Element {
  const brand = resolveBrand(entry.source_id);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 rounded-xl bg-surface-tertiary border border-edge px-3 py-3 text-left hover:border-edge-strong transition-colors"
    >
      {brand ? (
        <BrandMark brand={brand} size={22} />
      ) : (
        <Icon name="lock" size={18} className="text-content-secondary" />
      )}
      <span className="flex-1 text-sm font-semibold text-content">
        {titleCase(entry.display_name)}
      </span>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">
        configured
      </span>
    </button>
  );
}

function SourcePicker({
  entries,
  onPick,
  onClose,
}: {
  readonly entries: readonly SourceAppConfigEntry[];
  readonly onPick: (entry: SourceAppConfigEntry) => void;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-xl bg-surface border border-edge shadow-2xl p-4">
        <h2 className="text-[15px] font-semibold text-content m-0 mb-3">
          Add a source
        </h2>
        <div className="flex flex-col gap-1">
          {entries.map((entry) => {
            const brand = resolveBrand(entry.source_id);
            return (
              <button
                key={entry.source_id}
                type="button"
                onClick={() => {
                  onPick(entry);
                }}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left hover:bg-surface-hover"
              >
                {brand ? (
                  <BrandMark brand={brand} size={20} />
                ) : (
                  <Icon
                    name="lock"
                    size={16}
                    className="text-content-secondary"
                  />
                )}
                <span className="text-sm text-content">
                  {titleCase(entry.display_name)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SourcesPanel(): JSX.Element {
  const { list } = useSourceAppConfig();
  const [picking, setPicking] = useState(false);
  const [configuring, setConfiguring] = useState<SourceAppConfigEntry | null>(
    null,
  );

  const data = list.data;
  const all = data?.sources ?? [];
  const isConfigured = (e: SourceAppConfigEntry): boolean =>
    e.keys.some((k) => k.deployment_configured);
  const configured = all.filter(isConfigured);
  const available = all.filter((e) => !isConfigured(e));

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex items-start justify-between gap-3">
          <PageSectionHeader
            title="Sources"
            description="Credentials each source needs to run. Stored encrypted; applied on the next sync — no restart. A source appears here once it is configured."
          />
          <button
            type="button"
            onClick={() => {
              setPicking(true);
            }}
            disabled={list.isError || available.length === 0}
            className="mt-1 shrink-0 h-8 px-3 rounded-lg border border-edge text-sm text-content-secondary hover:text-content flex items-center gap-1.5 disabled:opacity-50"
          >
            <Icon name="plus" size={14} />
            Add
          </button>
        </div>

        <div className="h-5" />

        {data && !data.vault_available && (
          <div className="mb-3 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 text-xs text-yellow-400">
            The encrypted key store is not configured (SOURCE_SECRETS_MASTER_KEY
            is missing), so keys cannot be saved. Configure the master key to
            enable saving.
          </div>
        )}

        {list.isLoading ? (
          <div className="py-10 text-center text-content-tertiary text-sm">
            Loading…
          </div>
        ) : list.isError ? (
          <div className="py-10 text-center text-content-tertiary text-sm">
            Sources are managed by an admin.
          </div>
        ) : configured.length === 0 ? (
          <div className="rounded-xl border border-dashed border-edge px-4 py-10 text-center text-sm text-content-tertiary">
            No sources configured yet — click Add to configure one.
          </div>
        ) : (
          <div className="space-y-3">
            {configured.map((entry) => (
              <SourceCard
                key={entry.source_id}
                entry={entry}
                onClick={() => {
                  setConfiguring(entry);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {picking ? (
        <SourcePicker
          entries={available}
          onPick={(entry) => {
            setPicking(false);
            setConfiguring(entry);
          }}
          onClose={() => {
            setPicking(false);
          }}
        />
      ) : null}
      {configuring ? (
        <ConfigureSourceDialog
          sourceId={configuring.source_id}
          title={titleCase(configuring.display_name)}
          onClose={() => {
            setConfiguring(null);
          }}
        />
      ) : null}
    </div>
  );
}
